import React, { useEffect, useRef, useState } from 'react';
import * as echarts from 'echarts';
import { Card, Spinner } from 'react-bootstrap';
import { timeDistribution } from 'hooks/CallApi'; // API 함수 import
import { formatCountStyle3 } from 'components/common/SunnyUtils';
import { useAppContext } from 'providers/AppProvider';
import { getCompanyColor } from 'helpers/companyColors';

const SiteAverageCfProcess = () => {
  const { config: { isDemo } } = useAppContext();
  const chartRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [chartData, setChartData] = useState({ days: [], series: [], legends: [] });

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const response = await timeDistribution({ period: 'week', timeZone: clientTimeZone, type: 'cf', demo: isDemo });
        if (cancelled) return;
        const apiData = response.data;

        if (!apiData || apiData.length === 0) {
          if (cancelled) return;
          setChartData({ days: [], series: [], legends: [] });
          return;
        }

        // Calculate min and max processedDataSize
        const allDataSizes = apiData.flatMap(company =>
          company.topTwoHourlyData.flatMap(entry =>
            entry.hourlyData.map(hour => hour.processedDataSize)
          )
        );
        const minDataSize = Math.min(...allDataSizes);
        const maxDataSize = Math.max(...allDataSizes);

        // 날짜 데이터 생성
        const days = apiData[0]?.topTwoHourlyData.map((entry) => {
          const date = new Date(entry.date);
          return `${date.getMonth() + 1}-${date.getDate()} ${date.toLocaleDateString('ko-KR', { weekday: 'short' })}`;
        });


        const twoHourGroups = Array.from({ length: 12 }, (_, i) => {
          const startHour = i * 2;

          return `${String(startHour).padStart(2, '0')}:00`;
        });


        const transformData = (company) => {
          const { companyName, topTwoHourlyData } = company;

          const data = topTwoHourlyData.flatMap((entry, dayIndex) =>
            entry.hourlyData.map((hourEntry) => {
              const hourRange = `${hourEntry.hourRange.split('-')[0].padStart(2, '0')}:00`; // 범위 변환
              const yIndex = twoHourGroups.indexOf(hourRange); // yAxis와 일치하는 인덱스 찾기
              if (yIndex === -1) return null; // 매핑되지 않은 값 무시
              return [dayIndex, yIndex, hourEntry.processedDataSize];
            })
          ).filter(Boolean); // 유효한 값만 유지

          return {
            name: companyName,
            type: 'scatter',
            data,
            symbolSize: (val) => {
              const dataValue = val[2];
              const minSize = 14;
              const maxSize = 45;

              if (dataValue <= minDataSize) return minSize;
              if (dataValue >= maxDataSize) return maxSize;

              // 선형 스케일링 계산
              return minSize + ((dataValue - minDataSize) / (maxDataSize - minDataSize)) * (maxSize - minSize);
            },
            itemStyle: {
              color: getCompanyColor(companyName),
              opacity: 0.7,
            },
          };
        };

        const series = apiData.map(transformData);
        const legends = apiData.map((company) => company.companyName);

        if (cancelled) return;
        setChartData({ days, series, legends, twoHourGroups, rawData: apiData, minDataSize, maxDataSize });
      } catch (error) {
        if (cancelled) return;
        console.error('API 데이터 호출 실패:', error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [isDemo]);

  useEffect(() => {
    if (isLoading || !chartRef.current) return;

    const chart = echarts.init(chartRef.current);
    const { days, series, legends, twoHourGroups, rawData } = chartData;

    const option = {
      color: legends.map(company => getCompanyColor(company)),
      tooltip: {
        trigger: 'item',
        formatter: (params) => {
          const data = params.data;
          if (!data) return '';

          const dayIndex = data[0];
          const hourRange = data[1];
          const sum = data[2];
          const companyName = params.seriesName;

          const selectedCompany = rawData.find((company) => company.companyName === companyName);
          const selectedDayHourlyData = selectedCompany?.topTwoHourlyData[dayIndex]?.hourlyData;

          const top_five_list = selectedDayHourlyData
            ?.slice(0, 5)
            .map((entry, index) => {
              const [startHour, endHour] = entry.hourRange.split('-'); // 범위를 분리
              const formattedHourRange = `${startHour.padStart(2, '0')}:00 `; // 형식 변환
              const formattedValue = formatCountStyle3(entry.processedDataSize); // 데이터 크기 포맷
              return `${index + 1}. ${formattedHourRange} - ${formattedValue}`; // 포맷된 문자열 반환
            })
            .join('<br/>') || '데이터 없음';
          const daySum = selectedDayHourlyData
            ?.reduce((total, entry) => total + entry.processedDataSize, 0) || 0;

          const day = days[dayIndex];
          const formattedHourRange = (() => {
            if (typeof hourRange === 'string' && hourRange.includes('-')) {
              // 문자열이면서 범위('-')가 포함된 경우
              return `${hourRange.split('-')[0].padStart(2, '0')}:00`;
            } else if (typeof hourRange === 'number') {
              // 숫자인 경우 문자열로 변환 후 padStart
              return `${String(hourRange).padStart(2, '0')}:00`;
            } else if (typeof hourRange === 'string') {
              // 문자열이지만 범위('-')가 없는 경우
              return `${hourRange.padStart(2, '0')}:00`;
            }
            return '00:00'; // 기본값
          })();

          return `
            ${companyName}<br/>
            ${day} ${formattedHourRange}<br/>
     현재 시간 범위의 데이터량: ${formatCountStyle3(sum)}<br/>
            하루의 총 데이터량: ${formatCountStyle3(daySum)}<br/>
            <strong>Top 5:</strong><br/>
            ${top_five_list}
          `;
        },
      },
      legend: {
        data: legends,
        bottom: '2%',
      },
      grid: {
        left: '1%',
        right: '7%',
        bottom: '15%',
        top: '5%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: days,
        name: '날짜',
        axisLabel: {
          interval: 0,
        },
      },
      yAxis: {
        type: 'category',
        data: chartData.twoHourGroups,
        name: '시간대',
        splitLine: {
          show: true,
          lineStyle: {
            type: 'dashed',
          },
        },
      },
      series,
    };

    chart.setOption(option);

    const resizeChart = () => chart.resize();
    window.addEventListener('resize', resizeChart);

    return () => {
      window.removeEventListener('resize', resizeChart);
      chart.dispose();
    };
  }, [chartData, isLoading]);

  return (
    <Card className="h-md-100" style={{ borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: 'none', backgroundColor: '#FFFFFF' }}>
      <Card.Header className="pb-0 d-flex align-items-center" style={{ borderBottom: 'none', backgroundColor: 'transparent', padding: '20px 24px' }}>
        <h5 style={{ margin: 0, fontWeight: 700, color: '#111827' }}>일별 CF 최대 처리량 시간 분포</h5>
        <span
          style={{
            marginLeft: '8px',
            marginTop: 0,
            marginBottom: 0,
            padding: '2px 8px',
            backgroundColor: '#2C7BE5',
            color: 'white',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 'bold',
          }}
        >
          최근 1주일
        </span>
      </Card.Header>
      <Card.Body>
        {isLoading && (
          <div
            style={{
              width: '100%',
              height: '15rem',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        )}
        <div ref={chartRef} style={{ width: '100%', height: '20rem', display: isLoading ? 'none' : 'block' }} />
      </Card.Body>
    </Card>
  );
};

export default SiteAverageCfProcess;
